import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';

import type { Appointment } from '@/react-query/useAppointments';
import type { GetTaskResponse } from '@/react-query/useGetTasks';
import type { TPmsClinicalNote } from '@/react-query/usePmsClinicalNotes';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { tasksOnAtom } from '../../recommendedActions/atoms';
import { GamifiedTasksRedesigned } from '../../recommendedActions/huddleRedesign/gamifiedTasksRedesigned';
import { useReviewRadiographs } from '../../useReviewRadiographs';
import { AppointmentHeaderContent } from '../AppointmentHeaderContent';
import { AppointmentPatientNameGuard } from '../AppointmentPatientNameGuard';
import Opportunities from '../opportunities';
import { ReviewFindingsButton } from '../ReviewFindingsButton';
import SheetTx from '../SheetTx';

export const PatientInfoSheetContent = ({
  appointment,
  index,
  visitSummary,
  patientTask,
  hideProviderAvatar,
  onClickPreviousAppt,
  onClickNextAppt,
  isInsights,
  pmsClinicalNotes,
  isLoading,
}: {
  appointment?: Appointment;
  index?: number;
  visitSummary?: TVisitSummary;
  patientTask?: GetTaskResponse;
  selectedAppointment?: {
    unifiedId: string;
    estimatedVisitId?: number;
  };
  hideProviderAvatar?: boolean;
  onClickPreviousAppt?: (selectedAppointment: {
    unifiedId: string;
    estimatedVisitId: number | undefined;
  }) => void;
  onClickNextAppt?: (selectedAppointment: {
    unifiedId: string;
    estimatedVisitId: number | undefined;
  }) => void;
  isInsights?: boolean;
  pmsClinicalNotes?: TPmsClinicalNote[];
  isLoading?: boolean;
}) => {
  const isTasksOn = useAtomValue(tasksOnAtom);

  const { onReviewRadiographs, annotatorUri } =
    useReviewRadiographs(appointment);
  return (
    <SheetContent
      onOpenAutoFocus={(e) => e.preventDefault()}
      className="vai-max-h-screen vai-flex vai-flex-col"
    >
      <SheetHeader className="vai-flex vai-justify-between vai-bg-gray-100 vai-outline vai-outline-1 vai-outline-slate-300">
        <SheetTitle className="vai-typography-p vai-text-zinc-700 vai-px-4 vai-py-3">
          <Trans>Patient information</Trans>
        </SheetTitle>
      </SheetHeader>
      {isLoading ? (
        <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
          <FontAwesomeIcon
            icon={faSpinner}
            className="vai-h-9 vai-w-9 vai-text-zinc-200 vai-animate-spin"
          />
        </div>
      ) : (
        <div className="vai-relative vai-overflow-y-scroll vai-flex vai-flex-col vai-flex-grow vai-gap-6">
          <div className="vai-px-4">
            {!!appointment && index !== undefined && (
              <AppointmentPatientNameGuard
                appointment={appointment}
                index={index}
              />
            )}

            {!!appointment && index !== undefined && (
              <AppointmentHeaderContent
                isDrawer={true}
                appointment={appointment}
                hideProviderAvatar={hideProviderAvatar}
              />
            )}
          </div>

          <Opportunities visitSummary={visitSummary} />

          <div>
            <p className="vai-font-normal vai-text-black vai-px-4">
              <Trans>Treatment plan</Trans>
            </p>
            <hr className="vai-border-slate-100 vai-mb-4 vai-border-t vai-border-b-0 vai-shadow-none" />
            <SheetTx visitSummary={visitSummary} />
          </div>

          {isTasksOn && (
            <div data-testid="tasks-container">
              <p className="vai-font-normal vai-text-black vai-px-4 vai-py-1">
                <Trans>Tasks</Trans>
              </p>
              <hr className="vai-border-gray-100 vai-mb-4 vai-border-t vai-border-b-0 vai-shadow-none" />

              {!patientTask?.tasks.every((t) => t.status === 'NOT_SUGGESTED') &&
              appointment ? (
                <div className="vai-px-4 vai-recommended-actions vai-flex vai-flex-col vai-items-start">
                  {isTasksOn && (
                    <GamifiedTasksRedesigned
                      appointment={appointment}
                      context="PATIENT_SHEET"
                      patientTask={patientTask}
                    />
                  )}
                </div>
              ) : (
                <p className="vai-text-muted-foreground vai-text-sm vai-px-4 ">
                  <Trans>No tasks found</Trans>
                </p>
              )}
            </div>
          )}

          {isInsights && !!pmsClinicalNotes?.length && (
            <div data-testid="clinical-notes-container">
              <p className="vai-font-normal vai-text-black vai-px-4 vai-py-1">
                <Trans>Clinical Notes</Trans>
              </p>
              <hr className="vai-border-gray-100 vai-mb-4 vai-border-t vai-border-b-0 vai-shadow-none" />

              {pmsClinicalNotes.map((note) => (
                <div key={note.id} className="vai-flex-row vai-gap-2">
                  <p>{note.note}</p>
                </div>
              ))}
            </div>
          )}

          <p className="vai-text-[12px] vai-font-inter vai-px-4 vai-text-slate-500">
            <Trans>
              Recommendations from AI come from highly trained machine learning
              algorithms but are not a replacement for the clinician&apos;s
              professional assessment, which is always the final determination.
              See DSS-IFU for details.
            </Trans>
          </p>

          <div className="vai-sticky vai-bottom-0 vai-flex vai-justify-end vai-mt-auto vai-bg-white">
            <SheetFooter className="vai-px-4 vai-w-full vai-outline vai-outline-1 vai-outline-slate-300 vai-py-6">
              <div className="vai-flex vai-gap-2">
                {onClickPreviousAppt && appointment && (
                  <Button
                    className="vai-flex vai-bg-background-accent"
                    variant="ghost"
                    data-testid="previous-button"
                    onClick={() =>
                      onClickPreviousAppt({
                        unifiedId: appointment.appointment.unifiedId,
                        estimatedVisitId:
                          appointment.appointment.estimatedVisitId ?? 0,
                      })
                    }
                  >
                    <span className="vai-text-black">
                      <FontAwesomeIcon icon={faChevronUp} />
                    </span>
                  </Button>
                )}
                {onClickNextAppt && appointment && (
                  <Button
                    className="vai-flex vai-bg-background-accent"
                    variant="ghost"
                    data-testid="next-button"
                    onClick={() =>
                      onClickNextAppt({
                        unifiedId: appointment.appointment.unifiedId,
                        estimatedVisitId:
                          appointment.appointment.estimatedVisitId ?? 0,
                      })
                    }
                  >
                    <span className="vai-text-black">
                      <FontAwesomeIcon icon={faChevronDown} />
                    </span>
                  </Button>
                )}
              </div>
              <div className="vai-flex">
                <SheetClose asChild>
                  <Button className="vai-cursor-pointer" variant="sheet">
                    <Trans>Close</Trans>
                  </Button>
                </SheetClose>
                {!isInsights && (
                  <div className="vai-flex vai-flex-row vai-px-2 vai-gap-1 vai-w-fit vai-items-center">
                    {appointment?.patient.imagingPatientId ? (
                      <ReviewFindingsButton
                        annotatorUri={annotatorUri}
                        appointment={appointment}
                        onReviewRadiographs={onReviewRadiographs}
                        source="patient-details-sheet"
                      />
                    ) : (
                      <span className="vai-text-sm vai-text-text-secondary vai-px-2 vai-flex vai-flex-col vai-items-center">
                        <span>
                          <Trans>
                            <FontAwesomeIcon icon={faCircleInfo} /> No images
                          </Trans>
                        </span>
                        <p>
                          <Trans>available</Trans>
                        </p>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </SheetFooter>
          </div>
        </div>
      )}
    </SheetContent>
  );
};
